import API from '@services/API';
import { TICKET_MGMT_TYPES } from '@constants/actionTypes/ticketMgmtActionTypes';
import { configs } from '@constants/configs/configs';
import { setError, startSpinner, stopSpinner } from '../common/actionCreators';
import { removeKeys } from '@utils/miscellaneous/removeKeys';

const actions = { setError, startSpinner, stopSpinner };

export const clearTktTypesSearchResults = () => ({
    type: TICKET_MGMT_TYPES.EMPTY_SEARCH_RESULTS,
    workflowsByCategory: [],
});

export const clearTktTypeSelectionResults = () => ({
    type: TICKET_MGMT_TYPES.EMPTY_TICKET_TYPE_SELECTION_RESULTS,
    workflowsByCategory: [],
});

export const selectCategory = activeCategory => ({
    type: TICKET_MGMT_TYPES.SELECT_CATEGORY,
    activeCategory,
});

export const openTicketDrawer = drawerState => ({
    type: TICKET_MGMT_TYPES.OPEN_TICKET_DETAILS_DRAWER,
    isDrawerOpen: drawerState,
});

export const setTktTypeDetailsSuccessMsg = (message) => ({ 
    type: TICKET_MGMT_TYPES.SHOW_SUCCESS_MESSAGE, 
    ticketTypeFormMessage: message, 
});

export const openTicketTypeForm = formState => ({
    type: TICKET_MGMT_TYPES.OPEN_TICKET_TYPE_FORM,
    isFormOpen: formState, 
});

export const setTicketTypeDetails = tktTypeDetails => ({
    type: TICKET_MGMT_TYPES.EDIT_TICKET_TYPE_RECORD,
    ticketDetails: tktTypeDetails, 
});

export const setTktTypeFormState = (isDirty) => ({
    type: TICKET_MGMT_TYPES.TICKET_TYPE_FORM_STATE,
    isFormDirty: isDirty,
});

export const setCategorySuccessMsg = (message) => ({ 
    type: TICKET_MGMT_TYPES.SHOW_CATEGORY_SUCCESS_MSG, 
    categorySuccessMsg: message, 
});

export const showTktCategoryNavigation = (showNavigation) => ({   
    type: TICKET_MGMT_TYPES.SHOW_TICKET_CATEGORY_NAVIGATION,
    showTktCategoryNavigation: showNavigation
});

export const renameCategory = (oldCategory, category, oldCategoryEs = null, categoryEs = null, statusList) => async dispatch => {
    oldCategoryEs = oldCategoryEs === '' ? null : oldCategoryEs;
    categoryEs = categoryEs === '' ? null : categoryEs;
    const postParams = [configs.endpoints.TICKET_MGMT_UPDATE_CATEGORY, { oldCategory, category, oldCategoryEs, categoryEs }, 'PUT'];
    const data = await API.postData(postParams, dispatch, actions);
    if (data && data.success) {
        dispatch(setCategorySuccessMsg('ticket_management.ticketCategory.renamed.msg'));
        await dispatch(getTicketCategoriesByStatus(statusList));
        return data;
    } else {
        console.error('Cannot renameCategory');
        return (data && data.message);
    }
};

export const getTicketCategories = () => async dispatch => {
    const url = configs.endpoints.TICKET_MGMT_CATEGORIES;
    const data = await API.getData(url, dispatch, actions);
    if (data && data.data) {
        dispatch({ type: TICKET_MGMT_TYPES.GET_CATEGORIES, data: data.data });
    } else {
        console.error('Cannot getTicketCategories', url);
    }
};

export const getTicketCategoriesByStatus = (statusList) => async dispatch => {
    const url = configs.endpoints.TICKET_MGMT_CATEGORIES_BY_STATUS.processReplacements({ status: statusList});
    const response = await API.getData(url, dispatch, actions);
    if (response && response.data) {
        dispatch({ type: TICKET_MGMT_TYPES.GET_CATEGORIES_BY_STATUS, data: response.data });
    } else {
        dispatch({ type: TICKET_MGMT_TYPES.GET_CATEGORIES_BY_STATUS, data: []});
        console.error('Cannot getTicketCategoriesByStatus', url);
    }
};

export const getTicketTypesBySearch = (statusList, searchQueryInput) => async dispatch => {
    const url = configs.endpoints.TICKET_MGMT_GET_TICKET_TYPES_BY_SEARCH.processReplacements({ searchQuery: searchQueryInput, status: statusList});
    const data = await API.getData(url, dispatch, actions);
    if (data) {
        const workflowsByCategory = data.map(item => {
            const { id, category, categoryEs, type, typeEs, filename, description, status, customFields, tkmQueues, expiryDate, permission, defaultPriority, fileUploadEnabled, emailConfig } = item;
            return { id, category, categoryEs, type, typeEs, filename, description, status, customFields, workgroup: { label: tkmQueues && tkmQueues.name, value: tkmQueues && tkmQueues.id },
                expDate: expiryDate, permission, fileUploadEnabled, defaultPriority, ...JSON.parse(emailConfig) };
        });
        dispatch({ type: TICKET_MGMT_TYPES.SEARCH_TICKET_CATEGORIES_AND_TYPES, workflowsByCategory });
    } else {
        dispatch({ type: TICKET_MGMT_TYPES.SEARCH_TICKET_CATEGORIES_AND_TYPES, workflowsByCategory: [] });
    }
};

export const updateCategories = (payload, statusList) => async dispatch => {
    const postParams = [configs.endpoints.TICKET_MGMT_SAVE_NEW_TICKET_TYPE, payload];
    const data = await API.postData(postParams, dispatch, actions);

    if (data && data.success) {
        dispatch(setCategorySuccessMsg('ticket_management.ticketCategory.created.msg'));
        return dispatch(getTicketCategoriesByStatus(statusList));
    } else {
        console.error('Cannot updateCategories');
        return (data && data.message);
    }
};

export const getWorkflowTypes = url => async dispatch => {
    const workflowTypes = await API.getData(url, dispatch, actions);
    if (workflowTypes) {
        dispatch({ type: TICKET_MGMT_TYPES.GET_WORKFLOW_TYPES, workflowTypes });
    } else {
        console.error('Cannot getWorkflowTypes', url);
    }
};

export const getWorkgroups = url => async dispatch => {
    const workgroups = await API.getData(url, dispatch, actions);
    if (workgroups) {
        dispatch({ type: TICKET_MGMT_TYPES.GET_WORKGROUPS, workgroups });
    } else {
        console.error('Cannot getWorkgroups', url);
    }
};

export const getPermissions = url => async dispatch => {
    const permissions = await API.getData(url, dispatch, actions);
    if (permissions) {
        dispatch({ type: TICKET_MGMT_TYPES.GET_PERMISSIONS, permissions });
    } else {
        console.error('Cannot getPermissions', url);
    }
};

export const getWorkflowsByCategory = (activeCategoryName, statusList, id, showMessage) => async dispatch => {
    try {
        const url = configs.endpoints.TICKET_MGMT_WORKFLOW_BY_CATEGORY.processReplacements({ categoryName: activeCategoryName, status: statusList });
        const data = await API.getData(url, dispatch, actions);
        if(showMessage) {
            const ticketTypeMsg = id ? 'ticket_management.ticketTypes.updated.msg' : 'ticket_management.ticketTypes.created.msg';
            if(id) {
                dispatch(setTicketTypeDetails(null));
            }
            dispatch(openTicketDrawer(true));
            dispatch(openTicketTypeForm(false));
            dispatch(setTktTypeDetailsSuccessMsg(ticketTypeMsg));
        }
        if(data) {
            const workflowsByCategory = data.map(item => {
                const {
                    id,
                    type,
                    typeEs,
                    filename,
                    description,
                    status,
                    customFields,
                    tkmQueues,
                    expiryDate,
                    permission,
                    defaultPriority,
                    fileUploadEnabled,
                    emailConfig,
                } = item;

                return {
                    id,
                    type,
                    typeEs,
                    filename,
                    description,
                    status,
                    customFields,
                    workgroup: {
                        label: tkmQueues?.name,
                        value: tkmQueues?.id,
                    },
                    expDate: expiryDate,
                    permission,
                    fileUploadEnabled,
                    defaultPriority,
                    ...JSON.parse(emailConfig),
                };
            });
            const workFlowCategoryArr = workflowsByCategory?.length ? workflowsByCategory : [];
            dispatch({
                type: TICKET_MGMT_TYPES.GET_WORKFLOWS_BY_CATEGORY,
                workflowsByCategory: workFlowCategoryArr,
            });
        } else {
            console.error('Cannot getWorkflowsByCategory', url);
        }
    } catch (error) {
        if(showMessage) {
            const ticketTypeMsg = id ? 'ticket_management.ticketTypes.updated.msg' : 'ticket_management.ticketTypes.created.msg';
            if(id) {
                dispatch(setTicketTypeDetails(null));
            }
            dispatch(openTicketDrawer(true));
            dispatch(openTicketTypeForm(false));
            dispatch(setTktTypeDetailsSuccessMsg(ticketTypeMsg));
        } 
        console.error('Error in getWorkflowsByCategory:', error);
    }
};

export const saveNewTicketType = (payload) => async dispatch => {
    const { id, category, isCategorySelected = false, statusList } = payload;
    const requestPayload = removeKeys(payload, ['isCategorySelected', 'statusList']);
    const postParams = [configs.endpoints.TICKET_MGMT_SAVE_NEW_TICKET_TYPE, requestPayload];
    const response = await API.postData(postParams, dispatch, actions);
    if (response && response.success) {
        if(id && !isCategorySelected) {
            dispatch(openTicketDrawer(false));
            dispatch(openTicketTypeForm(false));
            dispatch(setTicketTypeDetails(null));
            dispatch(setTktTypeDetailsSuccessMsg('ticket_management.ticketTypes.updated.msg'));
            return;
        }
        return dispatch(getWorkflowsByCategory(category, statusList, id, true));
    } else {
        console.error('Cannot saveNewTicketType');
        return (response && response.message);
    }
};

export const reassignTicket = ({ taskId, queueName, assignee, ticketId, action = '' }) => async dispatch => {
    const reassignUrl = configs.endpoints.TICKET_MGMT_REASSIGN_TICKET.processReplacements({ taskId, queueName, assignee, ticketId, action });

    const data = await API.getData(reassignUrl, dispatch, actions);

    if (data) {
        return data;
    } else {
        console.error('Cannot reassignTicket', reassignUrl);
    }

};

export const fetchTicketTypes = (payload) => async dispatch => {
    const postParams = [configs.endpoints.TICKET_MGMT_GET_TICKET_TYPES, payload];
    const data = await API.postData(postParams, dispatch, actions);

    if (data) {
        return data;
    } else {
        console.error('Cannot getTicketTypes', configs.endpoints.TICKET_MGMT_GET_TICKET_TYPES);
    }
};


export const submitTicketReclassifyAction = ({ ticketType, ticketSubType, ticketId, targetProperties }) => async dispatch => {
    let formData = `ticketType=${ticketType}&ticketSubType=${ticketSubType}&ticketId=${ticketId}&propertiesMap[undefined]=Select&propertiesMap[ticketStatus]=${targetProperties.values}`;
    const postParams = [configs.endpoints.TICKET_MGMT_SUBMIT_RECLASSIFY, formData, 'POST', true, undefined, 'application/x-www-form-urlencoded'];


    const data = await API.postData(postParams, dispatch, actions);

    if (data) {
        return data;
    } else {
        console.error('Cannot submitTicketReclassifyAction', configs.endpoints.TICKET_MGMT_SUBMIT_RECLASSIFY);
    }
};


export const postFormData = ({ dataObj, url }) => async dispatch => {
    let formData = Object.keys(dataObj).map(key => (`${key}=${dataObj[key]}`)).join('&');
    const postParams = [url, formData, 'POST', true, undefined, 'application/x-www-form-urlencoded'];


    const data = await API.postData(postParams, dispatch, actions);

    if (data) {
        return data;
    } else {
        console.error('Cannot postFormData', url);
    }
};

export const getTags = page  => async dispatch => {
    const url = configs.endpoints.TICKET_MGMT_GET_TAGS.processReplacements({ page });
    const response = await API.getData(url, dispatch, actions);

    if(response.success) {
        return response.payload;
    } else {
        return null;
    }
};

export const updateTag = (body, method) => async dispatch => {
    const url = configs.endpoints.TICKET_MGMT_UPDATE_TAG;
    const response = await API.postData([ url, body, method ], dispatch, actions);

    if(response.success) {
        return response.payload;
    } else {
        return null;
    }
};

export const getWorkgroupsTagsList = () => async dispatch => {
    const url = configs.endpoints.TICKET_MGMT_WORKGROUPS_TAGS;
    const response = await API.getData(url, dispatch, actions);

    if(response.success) {
        return response.payload;
    } else {
        return null;
    }
};

export const getWorkgroupsTagsById = (id) => async dispatch => {
    const url = configs.endpoints.TICKET_MGMT_WORKGROUPS_TAGS_BY_ID.processReplacements({ id });
    const response = await API.getData(url, dispatch, actions);

    if (response.success) {
        return response.payload[0];
    } else {
        return null;
    }
};

export const getTagsAutocompleteSearch = (searchKey) => async dispatch => {
    const url = configs.endpoints.TICKET_MGMT_TAGS_AUTOCOMPLETE_SEARCH.processReplacements({ searchKey });
    const response = await API.getData(url, dispatch, actions, false);

    if (response) {
        return response;
    } else {
        return null;
    }
};

export const saveWorkgroupTags = (body) => async dispatch => {
    const url = configs.endpoints.TICKET_MGMT_WORKGROUP_TAGS_SAVE;
    const response = await API.postData([ url, body, undefined ], dispatch, actions);

    if (response.success) {
        return response.payload;
    } else {
        return null;
    }
};

export const validateWorkgroupName = (name) => async dispatch => {
    const url = configs.endpoints.TICKET_MGMT_WORKGROUPS_NAME_VALIDATION.processReplacements({ name });
    const response = await API.getData(url, dispatch, actions, false);
    return response?.success || false;
};

export const updateWorkgroup = (body) => async dispatch => {
    const url = configs.endpoints.TICKET_MGMT_UPDATE_WORKGROUP;
    var formData = new FormData();
  
    for (const [key, value] of Object.entries(body)) {
        formData.append(key, value);
    }

    const response = await API.postData([ url, formData, undefined ], dispatch, actions);

    if (response.statusCode === 200) {
        return true;
    } else {
        return null;
    }
};


export const getAssignees = params => async dispatch => {
    const url = configs.endpoints.TICKET_MGMT_EXCHANGE_ASSIGNEES.processReplacements({ params });
    const response = await API.getData(url, dispatch, actions);

    if(response.success) {
        return response.payload;
    } else {
        return null;
    }
};

export const updateAssigneeAttributes = (body, method) => async dispatch => {
    const url = configs.endpoints.TICKET_MGMT_UPDATE_ASSIGNEE_ATTRIBUTES;
    const response = await API.postData([ url, body, method ], dispatch, actions);

    if(response.success) {
        return {
            updatedUserInfo: response.payload,
            updatedUserId: body.userId
        };
    } else {
        return null;
    }
};

export const getAllTags = () => async dispatch => {
    const url = configs.endpoints.TICKET_MGMT_WORKGROUPS_ALL_TAGS;
    const response = await API.getData(url, dispatch, actions);
    return response.success ? response.payload : null;
};

export const assigneesActionCreators = {
    getAssignees,
    getWorkgroupsTagsList,
    getWorkgroupsTagsById,
    updateAssigneeAttributes,
    validateWorkgroupName,
    updateWorkgroup,
    getTagsAutocompleteSearch,
    saveWorkgroupTags,
    getAllTags,
};